/**
 * 粮食入库/出库共享的纯函数工具集
 * 从 EntryV1.vue / Exit.vue 抽取, 行为零变更
 */

/** 数字千分位格式化 (最多 2 位小数), null/NaN 返回 '0' */
export function formatN(n) {
  if (n == null || isNaN(n)) return '0'
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })
}

/**
 * [单价非必填 2026-09-08] 单价格式化: 没有单价数据 (null/undefined/''/0/NaN) 返回 '',
 * 有值时千分位最多 4 位小数 (对齐后端 setScale(4) 精度). 列表/详情单价列专用 —
 * 单价空就显示空白, 不再兜底 "0.00". 金额/重量等数值列继续用 formatN.
 *   formatPriceBlank(2.5)   → '2.5'
 *   formatPriceBlank(null)  → ''
 *   formatPriceBlank(0)     → ''   (0 视同未填价)
 */
export function formatPriceBlank(n) {
  if (n == null || n === '') return ''
  const v = Number(n)
  if (isNaN(v) || v === 0) return ''
  return v.toLocaleString(undefined, { maximumFractionDigits: 4 })
}

/**
 * [单价非必填 2026-09-08] "¥2.5/KG" 组合文本: 没有单价数据返回 '' (整段不渲染),
 * 有值返回 '¥x/KG'. 详情弹窗/凭证里"¥{{x}}/KG"内联单价专用.
 */
export function pricePerKgText(n) {
  const p = formatPriceBlank(n)
  return p === '' ? '' : `¥${p}/KG`
}

/**
 * [T-253] 固定小数位格式化 (千分位 + 位数固定, 含尾零), 供理论干粮展示用.
 * 与 formatN 的区别: formatN 是"最多 2 位"(13390 显示 '13,390'),
 * 本函数固定 N 位(11455 → '11,455.00'), 位数来自粮单配置的干粮小数位.
 *   formatDecimals(11455.02, 2) → '11,455.02'
 *   formatDecimals(11455, 3)    → '11,455.000'
 *   formatDecimals(11455.0289, 3) → '11,455.029' (展示用四舍五入, 不影响存储值)
 * @param {number} n 数值
 * @param {number} [decimals=2] 小数位 0~4, 越界取边界
 */
export function formatDecimals(n, decimals = 2) {
  const d = Math.min(4, Math.max(0, Math.floor(Number(decimals) || 0)))
  if (n == null || isNaN(n)) return (0).toFixed(d)
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })
}

/**
 * 金额抹零 (向下取整): 抹掉小数部分, 只留整数元.
 * 配合粮单配置的"建单抹零/打印抹零"使用. null/NaN 视为 0.
 *   123.99 → 123  123.01 → 123  100.50 → 100
 */
export function truncateZero(n) {
  return Math.floor(Number(n) || 0)
}

/**
 * 金额转人民币大写 (入库 EntryV1 / 出库 Exit 两份完全一致的实现)
 * @param {number} num
 * @returns {string}
 */
export function amountInWords(num) {
  num = Number(num) || 0
  const fraction = ['角', '分']
  const digit = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖']
  const unit = [['元', '万', '亿'], ['', '拾', '佰', '仟']]
  let head = num < 0 ? '欠' : ''
  num = Math.abs(num)
  let s = ''
  for (let i = 0; i < fraction.length; i++) {
    s += (digit[Math.floor(num * 10 * Math.pow(10, i)) % 10] + fraction[i]).replace(/零./, '')
  }
  s = s || '整'
  num = Math.floor(num)
  for (let i = 0; i < unit[0].length && num > 0; i++) {
    let p = ''
    for (let j = 0; j < unit[1].length && num > 0; j++) {
      p = digit[num % 10] + unit[1][j] + p
      num = Math.floor(num / 10)
    }
    s = p.replace(/(零.)*零$/, '').replace(/^$/, '零') + unit[0][i] + s
  }
  return head + s.replace(/(零.)*零元/, '元').replace(/(零.)+/g, '零').replace(/^整$/, '零元整')
}

/**
 * 统一取 error 文本 (入库/出库两份实现一致)
 * 优先级: message → errMsg → fallback
 */
export function errorText(e, fallback = '未知错误') {
  if (!e) return fallback
  if (typeof e === 'string') return e
  if (e.message) return e.message
  if (e.errMsg) return e.errMsg
  return fallback
}

/** 验码: id 末 8 位 (跟后端 tail8 同算法) */
export function tail8(id) {
  if (!id) return ''
  const s = String(id).trim().toLowerCase()
  return s.length >= 8 ? s.slice(-8) : s
}

/**
 * a-select 过滤函数 (入库/出库两份实现一致)
 * antd 1.x 的 show-search 自带过滤钩子
 */
export function filterOption(input, option) {
  const t = (option.componentOptions.children[0].text || '').toLowerCase()
  return t.indexOf(input.toLowerCase()) >= 0
}

/**
 * 身份证号脱敏: 保留前 6 后 4, 中间用 8 个 * 替换
 * 短于 10 位的原样返回 (兜底空串)
 */
export function maskIdCard(id) {
  if (!id || id.length < 10) return id || ''
  return id.slice(0, 6) + '********' + id.slice(-4)
}

// ===== [2026-10-28 原日期可见] 单据时间 vs 真实录单时间 =====
// 手工录入/Excel 导入可指定历史业务日期: BILL_TIME(单据时间)=所选日期, CREATE_TIME=真实录单时间.
// 过磅单两者同源 (BILL_TIME=建单时刻), 只有改过日期的单两者才不同日.

/** 列表/详情显示用的单据时间 (三级兜底, 与时间列/详情头部/打印同口径) */
export function billDisplayTime(row) {
  if (!row) return ''
  return String(row.billTime || row.weighTimeGross || row.createTime || '').replace('T', ' ')
}

/** 显示的单据时间与真实录单时间(createTime)不是同一天 → 日期被指定过, 界面值得给出原时间 */
export function isBillDateShifted(row) {
  if (!row || !row.createTime) return false
  return billDisplayTime(row).slice(0, 10) !== String(row.createTime).slice(0, 10)
}
